import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addDays, format } from 'date-fns';

interface Symptom {
  id: string;
  name: string;
  intensity: 1 | 2 | 3;
  date: string;
  notes?: string;
}

interface Mood {
  id: string;
  type: string;
  date: string;
  note?: string;
}

interface PeriodLog {
  date: string;
  flow: 'light' | 'medium' | 'heavy';
  notes?: string;
}

interface Cycle {
  id: string;
  startDate: string;
  endDate: string;
  length: number;
  periodLength: number;
  currentDay: number;
  phase: 'menstrual' | 'follicular' | 'ovulatory' | 'luteal';
  periodLogs: PeriodLog[];
  days: {
    date: string;
    symptoms: string[];
    mood?: string;
    temperature?: number;
  }[];
}

interface Settings {
  cycleLength: number;
  periodLength: number;
  notifications: boolean;
  trackTemperature: boolean;
}

interface PeriodState {
  cycles: Cycle[];
  currentCycle: Cycle | null;
  averageCycleLength: number;
  averagePeriodLength: number;
  symptoms: Symptom[];
  moods: Mood[];
  settings: Settings;
  addCycle: (cycle: Omit<Cycle, 'id'>) => void;
  updateCycle: (id: string, updates: Partial<Cycle>) => void;
  addSymptom: (symptom: Omit<Symptom, 'id'>) => void;
  addMood: (mood: Omit<Mood, 'id'>) => void;
  updateSettings: (updates: Partial<Settings>) => void;
  getSymptomsByDate: (date: string) => Symptom[];
  getMoodByDate: (date: string) => Mood | undefined;
  predictNextPeriod: () => Date | null;
  predictOvulation: () => Date | null;
  logPeriod: (date: string, flow: 'light' | 'medium' | 'heavy', notes?: string) => void;
  getPeriodLog: (date: string) => PeriodLog | undefined;
  removePeriodLog: (date: string) => void;
}

const defaultCycle: Cycle = {
  id: 'default',
  startDate: new Date().toISOString(),
  endDate: addDays(new Date(), 28).toISOString(),
  length: 28,
  periodLength: 5,
  currentDay: 14,
  phase: 'follicular',
  periodLogs: [],
  days: []
};

export const usePeriodStore = create<PeriodState>()(
  persist(
    (set, get) => ({
      cycles: [],
      currentCycle: defaultCycle,
      averageCycleLength: 28,
      averagePeriodLength: 5,
      symptoms: [],
      moods: [],
      settings: {
        cycleLength: 28,
        periodLength: 5,
        notifications: true,
        trackTemperature: false,
      },

      addCycle: (cycle) => set((state) => ({
        cycles: [...state.cycles, { ...cycle, id: crypto.randomUUID() }]
      })),

      updateCycle: (id, updates) => set((state) => ({
        cycles: state.cycles.map((cycle) =>
          cycle.id === id ? { ...cycle, ...updates } : cycle
        ),
        currentCycle: state.currentCycle?.id === id 
          ? { ...state.currentCycle, ...updates }
          : state.currentCycle
      })),

      logPeriod: (date, flow, notes) => set((state) => {
        if (!state.currentCycle) return state;

        const formattedDate = format(new Date(date), 'yyyy-MM-dd');
        const existingLogIndex = state.currentCycle.periodLogs.findIndex(
          log => format(new Date(log.date), 'yyyy-MM-dd') === formattedDate
        );

        const newPeriodLogs = [...state.currentCycle.periodLogs];
        const newLog = { date: formattedDate, flow, notes };

        if (existingLogIndex >= 0) {
          newPeriodLogs[existingLogIndex] = newLog;
        } else {
          newPeriodLogs.push(newLog);
        }

        return {
          currentCycle: {
            ...state.currentCycle,
            periodLogs: newPeriodLogs
          }
        };
      }),

      getPeriodLog: (date) => {
        const state = get();
        const formattedDate = format(new Date(date), 'yyyy-MM-dd');
        return state.currentCycle?.periodLogs.find(
          log => format(new Date(log.date), 'yyyy-MM-dd') === formattedDate
        );
      },

      removePeriodLog: (date) => set((state) => {
        if (!state.currentCycle) return state;

        const formattedDate = format(new Date(date), 'yyyy-MM-dd');
        return {
          currentCycle: {
            ...state.currentCycle,
            periodLogs: state.currentCycle.periodLogs.filter(
              log => format(new Date(log.date), 'yyyy-MM-dd') !== formattedDate
            )
          }
        };
      }),

      addSymptom: (symptom) => set((state) => ({
        symptoms: [...state.symptoms, { ...symptom, id: crypto.randomUUID() }]
      })),

      addMood: (mood) => set((state) => ({
        moods: [...state.moods, { ...mood, id: crypto.randomUUID() }]
      })),

      updateSettings: (updates) => set((state) => ({
        settings: { ...state.settings, ...updates }
      })),

      getSymptomsByDate: (date) => {
        const state = get();
        return state.symptoms.filter(
          (symptom) => symptom.date.split('T')[0] === date
        );
      },

      getMoodByDate: (date) => {
        const state = get();
        return state.moods.find(
          (mood) => mood.date.split('T')[0] === date
        );
      },

      predictNextPeriod: () => {
        const state = get();
        if (!state.currentCycle) return null;

        const startDate = new Date(state.currentCycle.startDate);
        return addDays(startDate, state.settings.cycleLength);
      },

      predictOvulation: () => {
        const state = get();
        if (!state.currentCycle) return null;

        const startDate = new Date(state.currentCycle.startDate);
        return addDays(startDate, Math.floor(state.settings.cycleLength / 2) - 1);
      }
    }),
    {
      name: 'period-tracker',
      version: 1,
    }
  )
);